import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Card, CardBody, Badge, Button, Spinner, Alert } from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { fetchPlatformStats } from "../../../helpers/platformStats";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";
const FETCH_LIMIT = 500; // tablo büyük (binlerce kayıt); en güncel girişler önce.

const fmtDate = (v) => (v ? new Date(v).toLocaleString("tr-TR") : "—");

const FcmTokenlari = () => {
  document.title = "FCM Token'ları | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  // Tablo sadece son FETCH_LIMIT kaydı gösteriyor; toplamlar sunucudan sayılıyor.
  const [ozet, setOzet] = useState(null);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `${BASE_URL}/KULLANICI_FCM_TOKENLERI?$orderby=SON_GIRIS desc&$top=${FETCH_LIMIT}` +
        `&$expand=KULLANICILAR($select=AD_SOYAD,ISLETME_AD)`;
      const res = await fetch(url, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("FCM token'ları yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOzet = async () => {
    try { setOzet(await fetchPlatformStats({ headers })); } catch { /* yoksay */ }
  };

  useEffect(() => { fetchListe(); fetchOzet(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm("Bu token kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/KULLANICI_FCM_TOKENLERI(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Token silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const copyToken = (t) => {
    if (!t) return;
    navigator.clipboard?.writeText(t);
    setSuccessMsg("Token panoya kopyalandı.");
    setTimeout(() => setSuccessMsg(""), 2000);
  };

  const columns = useMemo(() => [
    { header: "#", accessorKey: "ID", enableSorting: true, cell: (c) => <strong>#{c.getValue()}</strong> },
    {
      header: "Kullanıcı", accessorKey: "KULLANICI_ID", enableSorting: true,
      cell: (c) => {
        const u = c.row.original.KULLANICILAR;
        return <span className="fw-medium">{u?.AD_SOYAD || u?.ISLETME_AD || `#${c.getValue()}`}</span>;
      },
    },
    {
      header: "Token", accessorKey: "FCM_TOKEN", enableSorting: false,
      cell: (c) => {
        const t = c.getValue() || "";
        return (
          <span
            role="button"
            title="Kopyalamak için tıklayın"
            onClick={() => copyToken(t)}
            className="text-muted font-monospace"
            style={{ cursor: "pointer" }}
          >
            {t ? t.slice(0, 18) + "…" : "—"} <i className="bx bx-copy" />
          </span>
        );
      },
    },
    {
      header: "Cihaz", accessorKey: "CIHAZ_MODEL", enableSorting: true,
      cell: (c) => <small>{c.getValue() || "—"}</small>,
    },
    {
      header: "Son Giriş", accessorKey: "SON_GIRIS", enableSorting: true,
      cell: (c) => <small className="text-muted">{fmtDate(c.getValue())}</small>,
    },
    {
      header: "Durum", accessorKey: "AKTIF", enableSorting: true,
      cell: (c) => {
        const v = c.getValue();
        const aktif = v === true || v === "true" || v === 1 || v === "1";
        return <Badge color={aktif ? "success" : "secondary"}>{aktif ? "Aktif" : "Pasif"}</Badge>;
      },
    },
    {
      header: "İşlemler", enableSorting: false,
      cell: (cellProps) => (
        <Button color="danger" size="sm" outline onClick={() => handleDelete(cellProps.row.original.ID)}>
          <i className="bx bx-trash" />
        </Button>
      ),
    },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İlan Yönetimi" breadcrumbItem="FCM Token'ları" />
          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
          {ozet && (
            <Alert color="info" className="d-flex flex-wrap gap-3 align-items-center">
              <span><strong>{ozet.toplamToken}</strong> aktif token</span>
              <span><i className="bx bxl-android" /> Android <strong>{ozet.android}</strong> ({ozet.androidKullanici} kullanıcı)</span>
              <span><i className="bx bxl-apple" /> iOS <strong>{ozet.ios}</strong> ({ozet.iosKullanici} kullanıcı)</span>
              {ozet.web > 0 && <span>Web <strong>{ozet.web}</strong></span>}
              {ozet.diger > 0 && <span>Bilinmeyen <strong>{ozet.diger}</strong></span>}
              <span className="text-muted ms-auto">
                Tabloda en son giriş yapan {FETCH_LIMIT} kayıt listelenir.
              </span>
            </Alert>
          )}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div>
                      <h4 className="card-title mb-1">Bildirim (FCM) Token'ları</h4>
                      <p className="text-muted small mb-0">
                        Kullanıcı cihazlarının push bildirim token'ları. En güncel {liste.length} kayıt gösteriliyor.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={fetchListe} disabled={isLoading}>
                      <i className="bx bx-refresh" /> Yenile
                    </Button>
                  </div>
                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default FcmTokenlari;
